import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from 'store';
import { useEffect } from 'react';

const titilerApiURL = process.env.REACT_APP_TITILER_API;

type AssetInfo = {
  bounds: [any, any, any, any];
  minzoom: number;
  maxzoom: number;
  band_metadata: Array<[string, { [key: string]: any }]>;
  band_descriptions: Array<[string, string]>;
  dtype: string;
  nodata_type: 'Alpha' | 'Mask' | 'Internal' | 'Nodata' | 'None';
  colorinterp?: Array<string> | null;
  scale?: number | null;
  offset?: number | null;
  colormap?: { [key: string]: [number, number, number, number] } | null;
};
type AssetsInfo = {
  [key: string]: AssetInfo;
};
type TileJSON = {
  tilejson: string; // Default: "2.2.0"
  name?: string | null;
  description?: string | null;
  version: string; // Default: "1.0.0"
  attribution?: string | null;
  template?: string | null;
  legend?: string | null;
  scheme: 'xyz' | 'tms'; // Default: "xyz"
  tiles: string[];
  grids?: string[] | null;
  data?: string[] | null;
  minzoom: number; // Range: [0, 30], Default: 0
  maxzoom: number; // Range: [0, 30], Default: 30
  bounds?: number[]; // Default: [-180, -90, 180, 90]
  center?: [number, number, number] | null;
};

// Define a service using a base URL and expected endpoints
export const titilerApi = createApi({
  reducerPath: 'titilerApi',
  baseQuery: fetchBaseQuery({
    baseUrl: titilerApiURL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.authToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    getItemAssetsInfo: builder.query<
      AssetsInfo,
      { collectionID: string; itemID: string }
    >({
      query: ({ collectionID, itemID }) =>
        `/collections/${collectionID}/items/${itemID}/info`
    }),

    getItemAssetsStatistics: builder.query<
      { [key: string]: any },
      { collectionID: string; itemID: string; params: any }
    >({
      query: ({ collectionID, itemID, params = {} }) => {
        let formattedParams = {
          histogram_bins: params?.histogram_bins ?? 1,
          ...params
        };
        formattedParams = Object.fromEntries(
          Object.entries(formattedParams).filter(
            ([_, v]) => v !== undefined && v !== null && v !== ''
          )
        );
        const queryStr = new URLSearchParams(formattedParams).toString();
        return `/collections/${collectionID}/items/${itemID}/statistics?${queryStr}`;
      }
    }),

    getItemAssetTileJson: builder.query<
      TileJSON,
      {
        collectionID: string;
        itemID: string;
        assetsParams: string;
        params: Record<string, any>;
      }
    >({
      query: ({ collectionID, itemID, assetsParams, params }) => {
        const tileMatrixSetId = params?.tileMatrixSetId ?? 'WebMercatorQuad';

        const formattedParams = Object.fromEntries(
          Object.entries(params).filter(
            ([_, v]) => v !== undefined && v !== null && v !== ''
          )
        );
        const queryStr = new URLSearchParams(formattedParams).toString();
        return {
          url: `/collections/${collectionID}/items/${itemID}/${tileMatrixSetId}/tilejson.json?${assetsParams}&${queryStr}`
        };
      }
    }),

    getSearchMosaicsTileJson: builder.query<
      TileJSON,
      {
        searchID: string;
        assetsParams: string;
        params: Record<string, any>;
      }
    >({
      query: ({ searchID, assetsParams, params }) => {
        const tileMatrixSetId = params?.tileMatrixSetId ?? 'WebMercatorQuad';

        const formattedParams = Object.fromEntries(
          Object.entries(params).filter(
            ([_, v]) => v !== undefined && v !== null && v !== ''
          )
        );
        const queryStr = new URLSearchParams(formattedParams).toString();
        return {
          url: `/searches/${searchID}/${tileMatrixSetId}/tilejson.json?${assetsParams}&${queryStr}`
        };
      }
    }),

    getColorMapNames: builder.query<{ colorMaps: string[] }, void>({
      query: () => '/colorMaps'
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetItemAssetsInfoQuery,
  useGetItemAssetsStatisticsQuery,
  useGetItemAssetTileJsonQuery,
  useGetSearchMosaicsTileJsonQuery,
  useGetColorMapNamesQuery
} = titilerApi;

export const colorMapImageUrl = (colorMap: string, vertical = false) =>
  titilerApiURL +
  `/colorMaps/${colorMap}?format=png` +
  (vertical ? '&orientation=vertical' : '');

// Custom Hook to prefetch colormap images
export const usePreloadColorMapImages = () => {
  // Helper function to preload an image
  const preloadImage = (src: string) => {
    const img = new Image();
    img.src = src;
  };

  const { data, error, isLoading } = useGetColorMapNamesQuery();
  const colorMapNames = data?.colorMaps;

  useEffect(() => {
    if (colorMapNames && colorMapNames.length > 0) {
      colorMapNames.forEach((colormap_name) => {
        preloadImage(colorMapImageUrl(colormap_name));
      });
    }
  }, [colorMapNames]);

  return { colorMapNames, error, isLoading };
};
